/**
 * Format numérique compact "JJ/MM" depuis une date ISO (YYYY-MM-DD).
 * Adapté aux en-têtes de colonnes denses (ex. tableau croisé produits × jours).
 */
export function formatDayMonthShort(iso: string): string {
    return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

/**
 * @param timeZone Fuseau IANA d'affichage (variante `datetime` uniquement) — ex.
 *                 celui de l'établissement. Absent ou invalide : fuseau du navigateur.
 */
export function formatDate(dateInput: string | number | Date, format?: 'datetime' | 'short' | 'time' | 'dayMonth' | 'shortDatetimeFr', timeZone?: string | null): string {
    // Si la date n'est pas une instance de Date, on essaie de la convertir
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);

    if (isNaN(date.getTime())) {
        return '-';
        // throw new Error("Date invalide"); // Gérer les dates invalides
    }

    if (format === "datetime") {
        const options: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        };
        if (timeZone) {
            try {
                return new Intl.DateTimeFormat(navigator.language, { ...options, timeZone }).format(date);
            } catch {
                // Fuseau inconnu du navigateur (RangeError) : repli sur le fuseau local.
            }
        }
        return new Intl.DateTimeFormat(navigator.language, options).format(date);
    } else if (format === "shortDatetimeFr") {
        // Locale FIGÉE en `fr-FR`, contrairement à toutes les autres variantes
        // qui suivent `navigator.language`. Pour des écrans qui affichent déjà
        // tout leur texte en français en dur (CRM interne TriggerFlow, jamais
        // migré vers i18n — voir `frontend/scripts/check-french-hardcoded.mjs`) :
        // y laisser la langue du navigateur déterminer le format de date
        // produirait un écran mi-français mi-anglais selon la machine du staff.
        // Sans année (contrairement à `datetime`) : format court d'origine de
        // ces écrans, avant leur bascule sur cet utilitaire partagé.
        return new Intl.DateTimeFormat('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
    } else if (format === "short") {
        return new Intl.DateTimeFormat(navigator.language, {
            year: "2-digit",
            month: "2-digit",
            day: "2-digit",
        }).format(date);
    } else if (format === "time") {
        return new Intl.DateTimeFormat(navigator.language, {
            hour: "2-digit",
            minute: "2-digit",
        }).format(date);
    } else if (format === "dayMonth") {
        return new Intl.DateTimeFormat(navigator.language, {
            day: "numeric",
            month: "short", // "short" => jan., févr., mars... | "long" => janvier, février...
        }).format(date);
    }

    return new Intl.DateTimeFormat(navigator.language, {
        year: "numeric",
        month: "long", // "long" pour le mois en entier ("janvier"), "short" pour une abréviation ("jan.")
        day: "numeric",
    }).format(date);
}

/**
 * Jour et mois (« 11/10 ») d'un instant ISO 8601, dans la langue de l'interface
 * et le fuseau du navigateur. Sert aux échéances 2FA (bannière, badge).
 */
export function formatDayMonthInLocale(iso: string, locale: string): string {
    return new Date(iso).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
}

/** « mer. 15 oct. », locale figée en `fr-FR` (back-office). */
export function formatWeekdayDayMonthFr(iso: string): string {
    return new Date(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** « 14:30 », locale figée en `fr-FR` (back-office). */
export function formatTimeFr(iso: string): string {
    return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}
